import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../context/DataContext';
import { Calendar, ArrowLeft, Share2, User, MessageCircle } from 'lucide-react';
import Swal from 'sweetalert2';

const NewsDetail = () => {
  const { id } = useParams();
  const { data, loading } = useData();
  
  const newsItem = [
    ...(data?.news?.latest || []),
    ...(data?.news?.upcoming || []),
    ...(data?.news?.press || [])
  ].find(item => item.id.toString() === id);

  const allOtherNews = [
    ...(data?.news?.latest || []),
    ...(data?.news?.upcoming || []),
    ...(data?.news?.press || [])
  ].filter(item => item.id.toString() !== id);

  const relatableNews = allOtherNews.slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleShareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: newsItem?.title || 'Sahyadri News',
        url: window.location.href
      }).catch(err => console.log(err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Share link copied to clipboard!',
        showConfirmButton: false,
        timer: 2000
      });
    }
  };

  if (loading) {
    return (
      <div className="pt-32 min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-sugarcane-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!newsItem) {
    return (
      <div className="pt-32 min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-3xl font-bold mb-4">News Story Not Found</h2>
        <Link to="/news" className="text-sugarcane-green flex items-center font-bold">
          <ArrowLeft className="mr-2" /> Back to News
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-gradient-to-b from-gray-50/80 via-white to-gray-50/50 font-poppins pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Link (shown on mobile) */}
        <div className="mb-8 lg:hidden">
          <Link 
            to="/news" 
            className="inline-flex items-center text-gray-400 hover:text-sugarcane-green font-bold text-xs uppercase tracking-widest transition-colors group"
          >
            <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={14} />
            Back to Media Center
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column (Sticky Actions Bar - hidden on mobile) */}
          <div className="lg:col-span-1 hidden lg:flex flex-col items-center space-y-4 sticky top-28 pt-2">
            <Link 
              to="/news"
              className="w-11 h-11 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:text-sugarcane-green hover:border-sugarcane-green/30 hover:shadow-md transition-all duration-300 group"
              title="Back to Media Center"
            >
              <ArrowLeft className="group-hover:-translate-x-0.5 transition-transform" size={18} />
            </Link>

            <button 
              onClick={handleShareArticle}
              className="w-11 h-11 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:text-sugarcane-green hover:border-sugarcane-green/30 hover:shadow-md transition-all duration-300 cursor-pointer"
              title="Share Article"
            >
              <Share2 size={18} />
            </button>

            <div className="pt-6 border-t border-gray-200/60 w-8 flex flex-col items-center">
              <span 
                className="text-[9px] text-gray-400 uppercase tracking-widest font-black select-none" 
                style={{ writingMode: 'vertical-lr' }}
              >
                3 min read
              </span>
            </div>
          </div>

          {/* Main Article Content (lg:col-span-7) */}
          <div className="lg:col-span-7 bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8">
            {/* 1. Image */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden shadow-sm border border-gray-100 group"
            >
              <img 
                src={newsItem.image} 
                alt={newsItem.title}
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
            </motion.div>

            {/* 2. Title & Meta Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="border-b border-gray-100 pb-8"
            >
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="px-3.5 py-1 bg-sugarcane-green/10 text-sugarcane-green text-[10px] font-black uppercase tracking-wider rounded-lg border border-sugarcane-green/20">
                  {newsItem.category}
                </span>
                <div className="flex items-center text-gray-400 text-xs font-semibold">
                  <Calendar size={14} className="mr-1.5 text-gray-400" />
                  {newsItem.date}
                </div>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-sugarcane-charcoal leading-tight tracking-tight">
                {newsItem.title}
              </h1>

              <div className="flex items-center justify-between flex-wrap gap-4 pt-6 mt-6 border-t border-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 shadow-sm">
                    <User size={16} className="text-sugarcane-charcoal" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-sugarcane-charcoal uppercase tracking-widest">Sahyadri Editorial</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={handleShareArticle}
                    className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center hover:bg-sugarcane-green hover:text-white transition-all text-gray-400 shadow-sm cursor-pointer"
                    title="Share Link"
                  >
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* 3. Description Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-stone max-w-none"
            >
              <div className="text-base md:text-lg text-gray-600 leading-[1.8] space-y-6 font-poppins">
                {(newsItem.content || newsItem.desc || '').split('\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Sidebar (lg:col-span-4) */}
          <div className="lg:col-span-4 space-y-8">
            
            {/* Recent Insights Widget */}
            {relatableNews.length > 0 && (
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                <div className="border-b border-gray-100 pb-3 flex items-center justify-between">
                  <h3 className="font-bold text-sm text-sugarcane-charcoal uppercase tracking-wider">Recent Insights</h3>
                  <span className="w-8 h-1 bg-sugarcane-green rounded-full"></span>
                </div>
                <div className="space-y-5">
                  {relatableNews.map((item) => (
                    <Link 
                      key={item.id} 
                      to={`/news/${item.id}`}
                      className="flex gap-4 group/item hover:bg-gray-50/50 p-2 -m-2 rounded-2xl transition-colors duration-200"
                    >
                      <img 
                        src={item.image || 'https://via.placeholder.com/150'} 
                        alt={item.title}
                        className="w-20 h-16 object-cover rounded-xl border border-gray-100 shadow-sm bg-gray-50 flex-shrink-0"
                      />
                      <div className="flex flex-col justify-between py-0.5">
                        <span className="text-[9px] font-black text-sugarcane-green uppercase tracking-widest">
                          {item.category}
                        </span>
                        <h4 className="text-xs font-bold text-sugarcane-charcoal group-hover/item:text-sugarcane-green transition-colors line-clamp-2 leading-snug">
                          {item.title}
                        </h4>
                        <span className="text-[9px] text-gray-400 font-semibold">
                          {item.date}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Inquiry B2B Widget */}
            <div className="bg-gradient-to-br from-[#1b4332] via-[#2d6a4f] to-[#081c15] text-white p-8 rounded-3xl shadow-xl relative overflow-hidden border border-emerald-500/20">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full translate-x-12 -translate-y-12 blur-2xl"></div>
              <div className="relative z-10 space-y-5">
                <span className="px-3 py-1 bg-white/15 rounded-full text-[10px] font-extrabold uppercase tracking-widest text-emerald-300 border border-white/10 inline-block">
                  Need Assistance?
                </span>
                <h3 className="font-heading font-extrabold text-xl leading-snug text-white">
                  Have Any Queries or Need Help?
                </h3>
                <p className="text-white/80 text-xs leading-relaxed font-body font-medium">
                  Have questions regarding our sugarcane operations, products, or official news releases? Reach out to our dedicated support team today.
                </p>
                <div className="pt-2">
                  <Link 
                    to="/contact" 
                    className="w-full bg-emerald hover:bg-emerald-600 text-white font-heading font-extrabold text-xs uppercase tracking-widest py-4 rounded-xl transition-all shadow-md flex items-center justify-center space-x-2 cursor-pointer border border-emerald-400/30"
                  >
                    <MessageCircle size={16} />
                    <span>Contact Us</span>
                  </Link>
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default NewsDetail;
